import { Fragment, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { LazyEmojiPicker } from './LazyEmojiPicker';
import { CarouselMessageBubble } from './CarouselMessageBubble';
import { ContactActionModal } from './ContactActionModal';
import { downloadMediaFile } from '../utils/downloadMedia';
import { hasRichMarkup } from '../utils/richMessage';
import { decodeHtmlEntities } from '@/common/utils';
import { renderWhatsAppText } from '../../components/renderWhatsAppText';

function MessageStatusIcon({ status }) {
  if (status === 'sending') {
    return (
      <span className="inline-flex h-4 items-center leading-none" aria-hidden>
        …
      </span>
    );
  }
  const double = status === 'delivered' || status === 'read';
  const color = status === 'read' ? '#53BDEB' : '#8696A0';
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="shrink-0"
    >
      <path
        d="M1.4 8.5 4.2 11.2 9.1 5.2"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {double && (
        <path
          d="M5.1 8.5 7.9 11.2 14.2 4.2"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

function formatFileSize(bytes) {
  if (!bytes || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function linkifyFormatted(text, isIncoming) {
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = String(text).split(urlRegex);
  return parts.map((part, index) => {
    if (/^(https?:\/\/|www\.)/i.test(part)) {
      const href = part.toLowerCase().startsWith('www.')
        ? `https://${part}`
        : part;
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`underline font-medium [overflow-wrap:anywhere] ${
            isIncoming ? 'text-[#0284c7]' : 'text-white'
          }`}
        >
          {part}
        </a>
      );
    }
    return <Fragment key={index}>{renderWhatsAppText(part)}</Fragment>;
  });
}

function paramList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) =>
      typeof item === 'string' ? item : item?.text || item?.value || '',
    );
  }
  return [];
}

function applyTemplateParams(text, params) {
  if (!text || !params?.length || !text.includes('{{')) return text;
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const value = params[Number(n) - 1];
    return value ? value : `{{${n}}}`;
  });
}

function readTemplate(message) {
  const template = message?.template;
  if (!template || typeof template !== 'object') return null;
  const components = Array.isArray(template.components)
    ? template.components
    : [];
  const header = components.find((c) => c.type === 'HEADER') || {};
  const body = components.find((c) => c.type === 'BODY') || {};
  const footer = components.find((c) => c.type === 'FOOTER') || {};
  const buttonsComp = components.find((c) => c.type === 'BUTTONS') || {};
  const buttons = template.buttons || buttonsComp.buttons || [];
  const headerFormat = String(
    template.header_format || header.format || '',
  ).toUpperCase();
  const headerText =
    template.header_text ||
    (headerFormat === 'TEXT' || !headerFormat ? header.text : '') ||
    '';
  const params = paramList(
    template.body_parameters || template.parameters || body.parameters,
  );
  return {
    headerText: applyTemplateParams(headerText, paramList(header.parameters)),
    body: applyTemplateParams(template.body || body.text || '', params),
    footer: template.footer || template.footer_text || footer.text || '',
    buttons: Array.isArray(buttons) ? buttons : [],
    headerFormat,
  };
}

function renderRichNodes(text, isIncoming) {
  const template = document.createElement('template');
  template.innerHTML = text;
  let key = 0;
  const tagClass = {
    strong: 'font-semibold',
    em: 'italic',
    u: 'underline',
    s: 'line-through',
  };
  const tagName = {
    B: 'strong',
    STRONG: 'strong',
    I: 'em',
    EM: 'em',
    U: 'u',
    S: 's',
    STRIKE: 's',
    DEL: 's',
  };

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return renderMessageText(node.textContent, isIncoming);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    if (node.nodeName === 'BR') return '\n';
    const nextTag = tagName[node.nodeName];
    const children = Array.from(node.childNodes).map((child) => walk(child));
    if (!nextTag) return children;
    const Tag = nextTag;
    return (
      <Tag key={`r${key++}`} className={tagClass[nextTag]}>
        {children}
      </Tag>
    );
  };

  return Array.from(template.content.childNodes).map((node) => walk(node));
}

function renderMessageText(text, isIncoming) {
  if (!text) return null;
  if (hasRichMarkup(text)) return renderRichNodes(text, isIncoming);
  return linkifyFormatted(decodeHtmlEntities(text), isIncoming);
}

export function MessageBubble({
  message,
  isIncoming,
  onReact,
  canReact = true,
  onMediaClick,
  onMediaReady,
  showTime = true,
}) {
  const {
    id,
    content,
    time,
    mediaUrl,
    messageType,
    mimeType,
    fileName,
    fileSize,
    isDeleted,
    reaction,
    status,
  } = message;

  const [mediaError, setMediaError] = useState(false);
  const [tallImage, setTallImage] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showReactionPopup, setShowReactionPopup] = useState(false);
  const [showFullPicker, setShowFullPicker] = useState(false);
  const [contactAction, setContactAction] = useState(null);

  const closeReactionPopup = () => {
    setShowReactionPopup(false);
    setShowFullPicker(false);
  };

  useEffect(() => {
    setMediaError(false);
  }, [mediaUrl, id]);

  const isAudioMsg =
    !isDeleted &&
    messageType !== 'document' &&
    (mimeType?.startsWith('audio/') ||
      messageType === 'audio' ||
      fileName?.endsWith('.ogg') ||
      fileName?.endsWith('.wav') ||
      fileName?.endsWith('.mp3') ||
      fileName?.endsWith('.m4a') ||
      fileName?.endsWith('.aac') ||
      mimeType === 'video/ogg');

  const isTemplateImage =
    !isDeleted &&
    Boolean(mediaUrl) &&
    messageType !== 'document' &&
    (message?.template?.header?.format === 'IMAGE' ||
      /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i.test(mediaUrl) ||
      /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(fileName || ''));

  const isTemplateVideo =
    !isDeleted &&
    Boolean(mediaUrl) &&
    messageType !== 'document' &&
    (message?.template?.header?.format === 'VIDEO' ||
      /\.(mp4|mov|webm)(\?.*)?$/i.test(mediaUrl) ||
      /\.(mp4|mov|webm)$/i.test(fileName || ''));

  const isImageOrVideo =
    !isDeleted &&
    Boolean(mediaUrl) &&
    messageType !== 'document' &&
    (mimeType?.startsWith('image/') ||
      messageType === 'image' ||
      mimeType?.startsWith('video/') ||
      messageType === 'video' ||
      isTemplateImage ||
      isTemplateVideo);

  const buttons =
    (Array.isArray(message?.buttons) && message.buttons.length > 0
      ? message.buttons
      : Array.isArray(message?.template?.buttons)
        ? message.template.buttons
        : message?.template?.components?.find((c) => c.type === 'BUTTONS')
            ?.buttons) || [];

  const isDocOrFile =
    !isDeleted && Boolean(mediaUrl) && !isAudioMsg && !isImageOrVideo;

  const [resolvedFileSize, setResolvedFileSize] = useState(
    fileSize || message?.file_size || message?.filesize || null,
  );

  useEffect(() => {
    const initialSize = fileSize || message?.file_size || message?.filesize;
    if (initialSize) {
      setResolvedFileSize(initialSize);
      return;
    }

    // Only document / file attachments display file size in the UI.
    // Skip HEAD requests for images, videos, audio, or when mediaUrl is missing.
    if (!isDocOrFile || !mediaUrl) return;

    // Avoid cross-origin HEAD requests to third-party CDNs (e.g., lookaside.fbsbx.com, fbcdn)
    // which do not permit CORS and trigger browser console errors.
    let requestUrl = mediaUrl;
    try {
      if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) {
        const parsed = new URL(mediaUrl);
        if (parsed.origin !== window.location.origin) {
          if (parsed.pathname.startsWith('/media/')) {
            // Proxied backend media (/media/...) can use relative path
            requestUrl = parsed.pathname + parsed.search;
          } else {
            // Third-party external CDN (e.g. lookaside.fbsbx.com) does not permit CORS fetch
            return;
          }
        }
      }
    } catch {
      return;
    }

    let isMounted = true;
    fetch(requestUrl, { method: 'HEAD' })
      .then((res) => {
        const len = res.headers.get('content-length');
        if (len && isMounted) {
          setResolvedFileSize(parseInt(len, 10));
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [mediaUrl, fileSize, message, isDocOrFile]);

  const handleCopy = () => {
    if (!content) return;
    navigator.clipboard
      .writeText(content)
      .then(() => {
        toast.success('Message copied!');
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
        toast.error('Failed to copy');
      });
  };

  const handleDownload = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!mediaUrl) return;

    const loadingToastId = toast.loading('Downloading file...');
    try {
      await downloadMediaFile(mediaUrl, fileName);
      toast.success('Download complete!', { id: loadingToastId });
    } catch (err) {
      console.error('Download failed: ', err);
      toast.error('Download failed', { id: loadingToastId });
    }
  };

  const isSystemMediaText =
    content &&
    (content.startsWith('Media message:') ||
      content === `Media message: ${messageType}` ||
      content === 'Media message');

  const templateView = readTemplate(message);
  const bodySource =
    (content && content.trim() && !isSystemMediaText && content) ||
    templateView?.body ||
    '';
  const hasText = Boolean(bodySource && String(bodySource).trim());
  const isSingleMedia =
    isImageOrVideo || (messageType === 'template' && Boolean(mediaUrl));
  const bubbleMax = isSingleMedia
    ? 'w-full max-w-[min(340px,100%)]'
    : 'w-fit max-w-[min(100%,28rem)]';

  // Carousel template messages get their own dedicated renderer
  const isCarouselTemplate =
    messageType === 'template' &&
    Array.isArray(message.template?.cards) &&
    message.template.cards.length > 0;

  if (isCarouselTemplate) {
    return (
      <CarouselMessageBubble
        message={message}
        isIncoming={isIncoming}
        showTime={showTime}
        onMediaClick={onMediaClick}
      />
    );
  }

  return (
    <div
      className={`flex w-full items-center gap-2 group ${
        isIncoming ? 'justify-start' : 'justify-end'
      } ${reaction && reaction.length > 0 ? 'mb-3' : 'mb-1.5'}`}
    >
      {/* Outbound Reaction trigger (left side of bubble for outbound message) */}
      {!isIncoming && !isDeleted && canReact && (
        <div
          className={`relative shrink-0 transition-all duration-200 ${showMenu ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'}`}
        >
          <button
            onClick={() => setShowReactionPopup(!showReactionPopup)}
            className="p-1.5 rounded-lg bg-layer-3-header/50 border border-border-default/50 text-text-muted hover:text-text-primary hover:bg-input-background shadow-sm transition-colors"
            title="React to message"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"
              />
            </svg>
          </button>

          {showReactionPopup && (
            <>
              <div
                className="fixed inset-0 z-20"
                onClick={closeReactionPopup}
              />
              <div className="absolute bottom-full right-0 mb-2 bg-layer-3-header border border-border-default rounded-full px-2.5 py-1 shadow-xl flex items-center gap-1.5 z-30">
                {['👍', '❤️', '😂', '😮', '😢', '🙏'].map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => {
                      onReact?.(id, emoji);
                      closeReactionPopup();
                    }}
                    className="hover:scale-125 transition-transform duration-100 p-1 text-base select-none"
                  >
                    {emoji}
                  </button>
                ))}

                {/* Plus button for full picker */}
                <div className="relative flex items-center">
                  <button
                    onClick={() => setShowFullPicker(!showFullPicker)}
                    className="hover:scale-125 transition-transform duration-100 p-1 text-sm font-bold text-gray-600 hover:text-gray-900 select-none rounded-full bg-gray-100 hover:bg-gray-200 w-6 h-6 flex items-center justify-center"
                    title="More emojis"
                  >
                    +
                  </button>
                  {showFullPicker && (
                    <div className="absolute bottom-full right-0 mb-2 z-40 shadow-2xl">
                      <LazyEmojiPicker
                        theme="light"
                        onEmojiSelect={(emoji) => {
                          onReact?.(id, emoji.native);
                          closeReactionPopup();
                        }}
                        previewPosition="none"
                        skinTonePosition="none"
                        navPosition="none"
                        width="300px"
                        height="330px"
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Wrapper for Bubble and Time */}
      <div
        className={`flex flex-col ${isIncoming ? 'items-start' : 'items-end'} ${bubbleMax} min-w-0`}
      >
        {/* Bubble Container */}
        <div
          className={`relative max-w-full min-w-0 ${
            isSingleMedia || buttons.length > 0 ? 'w-full' : 'w-fit'
          } ${
            isAudioMsg || isImageOrVideo
              ? 'p-[2px]'
              : buttons.length > 0
                ? 'p-0 pt-2.5'
                : 'pl-4 pr-7 py-2.5'
          } rounded-[16px] ${
            isIncoming
              ? 'bg-[#F5F5F5] text-text-primary'
              : 'bg-primary text-white'
          } ${isDeleted ? 'opacity-75 italic' : ''} [word-break:break-word] min-w-0`}
        >
          {/* Dropdown Chevron for options */}
          {!isDeleted && (
            <div className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                className={`p-0.5 rounded-full transition-colors ${
                  isIncoming
                    ? 'bg-black/5 hover:bg-black/10 text-text-secondary hover:text-text-primary'
                    : 'bg-black/20 hover:bg-black/40 text-white/80 hover:text-white'
                }`}
                title="Message options"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                    }}
                  />
                  <div
                    className={`absolute mt-1 w-32 bg-layer-5-card border border-border-default rounded-xl shadow-xl py-1 z-30 overflow-hidden text-left ${
                      isIncoming ? 'left-0' : 'right-0'
                    }`}
                  >
                    {!mediaUrl && content && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy();
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-[11px] text-text-primary hover:bg-layer-7-hover flex items-center gap-1.5 transition-colors"
                      >
                        <svg
                          className="w-3.5 h-3.5 text-text-secondary"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                        >
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                        Copy Message
                      </button>
                    )}
                    {mediaUrl && (
                      <button
                        onClick={(e) => {
                          handleDownload(e);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-[11px] text-text-primary hover:bg-layer-7-hover flex items-center gap-1.5 transition-colors text-left"
                      >
                        <svg
                          className="w-3.5 h-3.5 text-text-secondary"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        Download
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {isDeleted ? (
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 opacity-70 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
              <span className="text-sm opacity-80 break-words">
                {content || 'This message was deleted'}
              </span>
            </div>
          ) : (
            <>
              {mediaUrl && (
                <div
                  className={
                    isAudioMsg
                      ? 'w-full'
                      : isImageOrVideo
                        ? hasText
                          ? 'w-full'
                          : ''
                        : 'mb-2'
                  }
                >
                  {mediaError ? (
                    <div className="flex flex-col items-center justify-center p-6 bg-black/10 rounded-xl border border-white/10 text-center w-full max-w-[280px] min-h-[160px] backdrop-blur-sm shadow-inner">
                      <div className="w-12 h-12 mb-3 bg-black/20 rounded-full flex items-center justify-center border border-white/5">
                        <svg
                          className="w-6 h-6 text-text-secondary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-text-primary">
                        Media unavailable
                      </span>
                      <span className="text-xs text-text-muted mt-1">
                        This file couldn&apos;t be loaded
                      </span>
                    </div>
                  ) : isAudioMsg ? (
                    <audio
                      src={mediaUrl}
                      controls
                      preload="metadata"
                      autoPlay={false}
                      referrerPolicy="no-referrer"
                      className="w-full max-w-[340px] block outline-none rounded-[14px] bg-[#F5F5F5]"
                      onError={() => setMediaError(true)}
                    />
                  ) : (mimeType?.startsWith('image/') ||
                      messageType === 'image' ||
                      isTemplateImage) &&
                    messageType !== 'document' ? (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onMediaClick?.(message);
                      }}
                      className={`cursor-pointer relative block overflow-hidden ${
                        hasText
                          ? 'w-full rounded-t-[14px] rounded-b-none'
                          : 'rounded-[14px]'
                      } group`}
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      {/* Opaque white surface behind image — prevents green/theme bleed on transparent PNGs */}
                      <div
                        className="absolute inset-0"
                        style={{ backgroundColor: '#ffffff' }}
                        aria-hidden
                      />
                      <img
                        src={mediaUrl}
                        alt={fileName || 'Attachment'}
                        referrerPolicy="no-referrer"
                        className={`relative z-[1] block w-full h-auto ${
                          tallImage
                            ? 'max-h-80 object-contain'
                            : 'object-contain'
                        } ${
                          hasText
                            ? 'rounded-t-[14px] rounded-b-none'
                            : 'rounded-[14px]'
                        }`}
                        style={{ backgroundColor: '#ffffff' }}
                        onLoad={(e) => {
                          const img = e.currentTarget;
                          if (img.naturalWidth > 0) {
                            setTallImage(
                              img.naturalHeight / img.naturalWidth > 1.8,
                            );
                          }
                          onMediaReady?.();
                        }}
                        onError={() => setMediaError(true)}
                      />
                    </div>
                  ) : (mimeType?.startsWith('video/') ||
                      messageType === 'video' ||
                      isTemplateVideo) &&
                    messageType !== 'document' ? (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onMediaClick?.(message);
                      }}
                      className={`cursor-pointer block overflow-hidden ${
                        hasText
                          ? 'w-full rounded-t-[14px] rounded-b-none'
                          : 'rounded-[14px]'
                      } group`}
                    >
                      <video
                        src={mediaUrl}
                        controls
                        preload="metadata"
                        autoPlay={false}
                        referrerPolicy="no-referrer"
                        className={`block w-full h-auto max-h-80 object-contain ${
                          hasText
                            ? 'rounded-t-[14px] rounded-b-none'
                            : 'rounded-[14px]'
                        }`}
                        onError={() => setMediaError(true)}
                      />
                    </div>
                  ) : mimeType === 'application/pdf' ||
                    fileName?.endsWith('.pdf') ||
                    messageType === 'document' ? (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onMediaClick?.(message);
                      }}
                      className="flex items-center gap-3 p-3 rounded-[12px] bg-[#F5F6F6] hover:bg-[#EAEBEB] transition-colors cursor-pointer border border-[#E2E4E6] w-full max-w-[320px] min-w-0 group select-none"
                    >
                      <div className="w-10 h-11 shrink-0 bg-[#E5252A] rounded-[8px] flex flex-col items-center justify-center text-white shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-2.5 h-2.5 bg-black/20 rounded-bl-sm" />
                        <span className="text-[10px] font-extrabold tracking-wider uppercase leading-none mt-0.5">
                          PDF
                        </span>
                      </div>

                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <span className="text-[14px] font-semibold text-[#111B21] leading-tight truncate group-hover:text-primary transition-colors">
                          {fileName || content || 'Document.pdf'}
                        </span>
                        <span className="text-[12px] text-text-muted mt-1 leading-none">
                          PDF
                          {resolvedFileSize
                            ? ` • ${formatFileSize(resolvedFileSize)}`
                            : ''}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={handleDownload}
                      className="flex items-center gap-3 p-3 rounded-[12px] bg-[#F5F6F6] hover:bg-[#EAEBEB] transition-colors cursor-pointer border border-[#E2E4E6] w-full max-w-[320px] min-w-0 group select-none"
                    >
                      <div className="w-10 h-11 shrink-0 bg-[#3B82F6] rounded-[8px] flex flex-col items-center justify-center text-white shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-2.5 h-2.5 bg-black/20 rounded-bl-sm" />
                        <span className="text-[9px] font-extrabold tracking-wider uppercase leading-none mt-0.5 truncate w-[36px] text-center px-0.5">
                          {fileName
                            ? fileName
                                .split('.')
                                .pop()
                                .toUpperCase()
                                .substring(0, 4)
                            : 'FILE'}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <span className="text-[14px] font-semibold text-[#111B21] leading-tight truncate group-hover:text-primary transition-colors">
                          {fileName || 'Document'}
                        </span>
                        <span className="text-[12px] text-text-muted mt-1 leading-none uppercase flex items-center gap-1">
                          {fileName ? fileName.split('.').pop() : 'FILE'}
                          {resolvedFileSize
                            ? ` • ${formatFileSize(resolvedFileSize)}`
                            : ''}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {templateView?.headerText && (
                <p className="px-2.5 pt-2 text-[14px] font-semibold leading-5 whitespace-pre-wrap [overflow-wrap:anywhere]">
                  {linkifyFormatted(templateView.headerText, isIncoming)}
                </p>
              )}
              {hasText && !isAudioMsg && (
                <div
                  className={
                    isImageOrVideo
                      ? 'px-3.5 pt-2 pb-1.5'
                      : buttons.length > 0
                        ? 'pl-4 pr-7 pb-2'
                        : ''
                  }
                >
                  <p className="text-[14px] leading-5 whitespace-pre-wrap [overflow-wrap:anywhere] max-w-full font-[Nirmala_UI,Noto_Sans_Gujarati,Noto_Sans_Devanagari,Segoe_UI_Emoji,system-ui,sans-serif]">
                    {renderMessageText(bodySource, isIncoming)}
                  </p>
                </div>
              )}
              {templateView?.footer && (
                <p
                  className={`px-2.5 pt-1 text-[12px] leading-4 whitespace-pre-wrap [overflow-wrap:anywhere] ${
                    isIncoming ? 'text-[#667781]' : 'text-white/70'
                  }`}
                >
                  {templateView.footer}
                </p>
              )}
              {showTime && (
                <div
                  className={`flex items-center justify-end gap-1 px-2 pt-1 text-[11px] leading-none ${
                    isIncoming ? 'text-[#667781]' : 'text-white/80'
                  }`}
                >
                  <span>{time}</span>
                  {!isIncoming && status === 'failed' && (
                    <span className="text-red-500">Failed</span>
                  )}
                  {!isIncoming && status && status !== 'failed' && (
                    <span
                      className="inline-flex items-center"
                      aria-label={status}
                    >
                      <MessageStatusIcon status={status} />
                    </span>
                  )}
                </div>
              )}
              {buttons.length > 0 && (
                <div
                  className={`border-t ${
                    isIncoming
                      ? 'border-[#E2E8F0] divide-y divide-[#E2E8F0]'
                      : 'border-white/20 divide-y divide-white/20'
                  } rounded-b-[14px] overflow-hidden`}
                >
                  {buttons.map((btn, idx) => {
                    const isPhone =
                      btn.type === 'PHONE_NUMBER' || btn.type === 'phone';
                    const isUrl = btn.type === 'URL' || btn.type === 'url';
                    return (
                      <button
                        key={btn.id || idx}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPhone && btn.phone_number) {
                            setContactAction({
                              phoneNumber: btn.phone_number,
                              title: btn.text || 'Contact',
                            });
                          } else if (isUrl && btn.url) {
                            window.open(
                              btn.url,
                              '_blank',
                              'noopener,noreferrer',
                            );
                          }
                        }}
                        className={`w-full py-2.5 px-3 flex items-center justify-center gap-2 text-[13px] font-medium transition-colors select-none whitespace-normal ${
                          isIncoming
                            ? 'text-[#0088CC] hover:bg-black/[0.04] active:bg-black/[0.08]'
                            : 'text-white hover:bg-black/10 active:bg-black/20'
                        }`}
                      >
                        {isPhone && (
                          <svg
                            className="w-4 h-4 shrink-0 text-current"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 5a2 2 0 012-2h3l2 5-2.5 1.5a11 11 0 005 5L14 14l5 2v3a2 2 0 01-2 2h-1C8.61 21 3 15.39 3 8V5z"
                            />
                          </svg>
                        )}
                        {isUrl && (
                          <svg
                            className="w-4 h-4 shrink-0 text-current"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        )}
                        {!isPhone && !isUrl && (
                          <svg
                            className="w-4 h-4 shrink-0 text-current"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                            />
                          </svg>
                        )}
                        <span className="min-w-0 text-center [overflow-wrap:anywhere]">
                          {btn.text || 'Button'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Reaction Badge */}
          {reaction && reaction.length > 0 && (
            <div
              className={`absolute bottom-[-10px] ${
                isIncoming ? 'right-2.5' : 'left-2.5'
              } bg-layer-3-header border border-border-default rounded-full px-1.5 py-0.5 text-[11px] shadow-md z-10 flex items-center justify-center gap-0.5 select-none`}
              title="Message reactions"
            >
              {reaction.map((emoji, idx) => (
                <span key={idx}>{emoji}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Inbound Reaction trigger (right side of bubble for inbound message) */}
      {isIncoming && !isDeleted && canReact && (
        <div
          className={`relative shrink-0 transition-all duration-200 ${showMenu ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'}`}
        >
          <button
            onClick={() => setShowReactionPopup(!showReactionPopup)}
            className="p-1.5 rounded-lg bg-layer-3-header/50 border border-border-default/50 text-text-muted hover:text-text-primary hover:bg-input-background shadow-sm transition-colors"
            title="React to message"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"
              />
            </svg>
          </button>

          {showReactionPopup && (
            <>
              <div
                className="fixed inset-0 z-20"
                onClick={closeReactionPopup}
              />
              <div className="absolute bottom-full left-0 mb-2 bg-layer-3-header border border-border-default rounded-full px-2.5 py-1 shadow-xl flex items-center gap-1.5 z-30">
                {['👍', '❤️', '😂', '😮', '😢', '🙏'].map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => {
                      onReact?.(id, emoji);
                      closeReactionPopup();
                    }}
                    className="hover:scale-125 transition-transform duration-100 p-1 text-base select-none"
                  >
                    {emoji}
                  </button>
                ))}

                {/* Plus button for full picker */}
                <div className="relative flex items-center">
                  <button
                    onClick={() => setShowFullPicker(!showFullPicker)}
                    className="hover:scale-125 transition-transform duration-100 p-1 text-sm font-bold text-text-secondary hover:text-white select-none rounded-full bg-white/[0.06] w-6 h-6 flex items-center justify-center border border-white/10"
                    title="More emojis"
                  >
                    +
                  </button>
                  {showFullPicker && (
                    <div className="absolute bottom-full left-0 mb-2 z-40 shadow-2xl">
                      <LazyEmojiPicker
                        theme="light"
                        onEmojiSelect={(emoji) => {
                          onReact?.(id, emoji.native);
                          closeReactionPopup();
                        }}
                        previewPosition="none"
                        skinTonePosition="none"
                        navPosition="none"
                        width="300px"
                        height="330px"
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Contact Action Modal (Direct Call vs WhatsApp) */}
      <ContactActionModal
        action={contactAction}
        onClose={() => setContactAction(null)}
      />
    </div>
  );
}
